import { useQuery } from '@tanstack/react-query';
import { consoleApi } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { CapabilityNotice } from '../components/CapabilityNotice';
import { Icon } from '../components/Icon';
import { FilterSelect, ListFilters, ListPagination } from '../components/ListControls';
import { QueryState } from '../components/QueryState';
import { PageHeader } from '../components/Shell';
import { labelize } from '../lib/format';
import { useListState } from '../lib/list-state';
import { NavLink } from '../lib/router';
import { auditAction, auditGroups, auditObject, auditObjectLink, auditOutcome, auditPageCounts, auditTime } from '../lib/audit-timeline';
import type { AuditEvent } from '../types';
import './audit.css';

export function AuditTimeline({ items, manageProspects }: { items: AuditEvent[]; manageProspects: boolean }) {
  const counts = auditPageCounts(items);
  return <>
    <div className="activity-page-counts" aria-label="Outcomes on this page">
      <strong>This page</strong><span><Icon name="check" />{counts.complete} completed actions</span>
      <span><Icon name="warning" />{counts.attention} failed, rejected or blocked</span><span>{counts.neutral} other results</span>
    </div>
    <div className="activity-timeline" tabIndex={0} role="region" aria-label="Activity timeline, grouped by UTC day">
      {auditGroups(items).map(group => <section className="activity-day" key={group.key} aria-label={group.label}>
        <header><h2>{group.label}{group.key !== 'unavailable' ? <span> UTC</span> : null}</h2><span>{group.items.length} on this page</span></header>
        <ol>{group.items.map(item => {
          const result = auditOutcome(item.outcome), link = auditObjectLink(item, manageProspects);
          return <li key={item.id}>
            <details className={'activity-event activity-event--' + result.tone}>
              <summary aria-label={`${auditAction(item.action)} · ${result.label}. Evidence for event ${item.id}`}>
                <span className="activity-event__time">{auditTime(item.occurredAt)}</span>
                <span className="activity-event__icon"><Icon name={result.icon} /></span>
                <span className="activity-event__heading"><strong>{auditAction(item.action)}</strong><span>{auditObject(item)}</span></span>
                <span className="activity-event__outcome">{result.label}</span>
                <span className="activity-event__disclose">Evidence<Icon name="chevronRight" /></span>
              </summary>
              <div className="activity-event__evidence">
                {link ? <NavLink to={link} className="activity-object-link">Open {auditObject(item)}<Icon name="chevronRight" /></NavLink> : null}
                <dl>{[
                  ['Event', item.id], ['Occurred (recorded)', item.occurredAt], ['Action code', item.action],
                  ['Outcome code', item.outcome], ['Object type', item.objectType], ['Object ID', item.objectId],
                  ['Actor', item.actor], ['Reason code', item.reasonCode], ['Request ID', item.requestId]
                ].map(([label, value]) => <div key={label}><dt>{label}</dt><dd><code>{value || 'Not supplied'}</code></dd></div>)}</dl>
              </div>
            </details>
          </li>;
        })}</ol>
      </section>)}
    </div>
  </>;
}

export function AuditPage() {
  const { capabilities } = useAuth();
  const state = useListState();
  const options = { cursor: state.cursor, search: state.value('search'), since: state.value('since'), until: state.value('until'), outcome: state.value('outcome'), resource_type: state.value('resource_type') };
  const query = useQuery({ queryKey: ['audit', options], queryFn: () => consoleApi.audit(options), enabled: capabilities.viewAudit });
  const items = query.isError ? [] : query.data?.items || [];
  const pager = query.isLoading || query.isError ? <nav className="list-pagination" aria-label="Record pages">
    <button type="button" className="button button--quiet" disabled={state.page === 1 || query.isFetching} onClick={state.previous}>Previous</button>
    <span role="status">Page {state.page} · {query.isError ? 'Results unavailable' : 'Loading records…'}</span>
    <button type="button" className="button button--quiet" disabled>Next</button>
  </nav> : <ListPagination state={state} nextCursor={query.data?.nextCursor} count={items.length} busy={query.isFetching} />;
  return <div className="activity-workspace">
    <PageHeader title="Activity" description="Follow system actions and review decisions. Open an event for its evidence; use filters to explore the full history." action={capabilities.viewAudit ? <button type="button" className="button button--quiet" disabled={query.isFetching} onClick={() => void query.refetch()}><Icon name="refresh" />{query.isFetching ? 'Refreshing…' : 'Refresh activity'}</button> : undefined} />
    <CapabilityNotice allowed={capabilities.viewAudit}>Your role does not include access to the audit ledger.</CapabilityNotice>
    {capabilities.viewAudit ? <>
      <ListFilters state={state} searchLabel="Search actions or reasons">
        <FilterSelect label="Object" value={state.value('resource_type')} onChange={resource_type => state.filter({ resource_type })} options={['', 'work', 'outbox', 'backlog', 'agent_run', 'mail', 'prospect', 'experiment'].map(value => [value, value ? labelize(value) : 'All objects'])} />
        <FilterSelect label="Outcome" value={state.value('outcome')} onChange={outcome => state.filter({ outcome })} options={['', 'SUCCEEDED', 'FAILED', 'REJECTED', 'APPROVED', 'ACCEPTED', 'BLOCKED'].map(value => [value, value ? labelize(value) : 'All outcomes'])} />
      </ListFilters>
      <section className="panel activity-history">
        {pager}
        <QueryState loading={query.isLoading} error={query.isError} empty={query.data?.items.length === 0} retry={() => void query.refetch()}>
          <AuditTimeline items={items} manageProspects={capabilities.manageProspects} />
        </QueryState>
        {items.length > 10 ? pager : null}
        <p className="activity-scope">These are audit outcomes, not delivered emails, unread replies or new customers. Counts cover this page only.</p>
      </section>
    </> : null}
  </div>;
}
